import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Text, TextButton, TextWithIcon, Tooltip } from '@modusign/design-system';
import { useState } from 'react';

import { ResourceModalBody } from '@app/components/Modal/ResourceModal';
import {
  AuthorizedResourceRoleSummary,
  PermissionAssigneeSearchInput,
} from '@app/features/authorization';
import ResourceRoleList from '@app/features/authorization/components/ResourceRoleList';
import { ResourceRole } from '@app/features/authorization/types';
import countSummary from '@app/features/authorization/utils/countSummary';

interface ResourceRoleListContentProps<T extends string> {
  hasEditPermission: boolean;
  resourceRoles: ResourceRole<T>[];
  resourceRoleTypes: Array<{ name: T; description: string }>;
  onAddResourceRoleClick: () => void;
  onRemoveResourceRole: (resourceRole: ResourceRole<T>[]) => void;
  onChangeResourceRole: (resourceRole: ResourceRole<T>[]) => void;
}

export default function ResourceRoleListContent<T extends string>({
  hasEditPermission,
  resourceRoles,
  resourceRoleTypes,
  onAddResourceRoleClick,
  onChangeResourceRole,
  onRemoveResourceRole,
}: ResourceRoleListContentProps<T>) {
  const [keyword, setKeyword] = useState('');

  const filteredResourceRoleList = resourceRoles.filter((rr) => {
    if (rr.subject.subjectType === 'GROUP') {
      return rr.subject.groupName.includes(keyword);
    }

    return rr.subject.memberName.includes(keyword) || rr.subject.memberEmail.includes(keyword);
  });

  const { group, member } = countSummary(filteredResourceRoleList.map((rr) => rr.subject));

  return (
    <ResourceModalBody gap={6}>
      <Flex justifyContent="space-between">
        <Text>
          <Text fontSize="md" fontWeight="medium" mr="2px">
            {keyword ? '검색 결과 :' : '공유된'}
          </Text>
          <AuthorizedResourceRoleSummary
            groupCount={group}
            isActive={!!keyword}
            memberCount={member}
          />
        </Text>
        <Tooltip content="권한 없음" disabled={hasEditPermission}>
          <TextButton
            disabled={!hasEditPermission}
            display={['none', null, 'inline']}
            onClick={onAddResourceRoleClick}
          >
            <TextWithIcon
              iconContainerWidth="11px"
              leftIcon={<FontAwesomeIcon icon={faPlus} width="10px" />}
            >
              추가
            </TextWithIcon>
          </TextButton>
        </Tooltip>
      </Flex>
      <PermissionAssigneeSearchInput value={keyword} onChange={setKeyword} />
      <ResourceRoleList
        hasEditPermission={hasEditPermission}
        resourceRoleTypes={resourceRoleTypes}
        resourceRoles={filteredResourceRoleList}
        onChangeResourceRole={(v) => onChangeResourceRole([v])}
        onRemoveResourceRole={(v) => onRemoveResourceRole([v])}
      />
    </ResourceModalBody>
  );
}
